import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from '@/shared/libs/generate-path'
import { useAllProvincesQuery, useCountriesQuery } from '@/entities/country'
import { useDestinationsQuery } from '@/entities/destination'
import { useLanguagesQuery } from '@/entities/tenant'
import { DestinationListTable } from '@/features/destination'
import { TenantSearchBar } from '@/features/tenant/tenant-search-bar'
import { ALL_FILTER_VALUE } from '@/shared/constants/filters'
import { AdminScopeType, DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useLanguageFilter } from '@/shared/hooks/use-language-filter'
import { buildScopedAdminPath } from '@/shared/libs/admin-routes'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

const DESTINATION_FILTER_VALUE = {
  ALL: 'all',
  SELECT_ALL: ALL_FILTER_VALUE,
} as const

const DESTINATION_STATUS_FILTER = {
  ALL: DESTINATION_FILTER_VALUE.ALL,
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const

type DestinationStatusFilter =
  (typeof DESTINATION_STATUS_FILTER)[keyof typeof DESTINATION_STATUS_FILTER]

/**
 * Renders the destination list with localized fetches and advanced filters.
 */
export function DestinationListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isSuperAdmin, scopeType, tenantSlug } = useAdminScope()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [searchTerm, setSearchTerm] = useState('')
  const [countryId, setCountryId] = useState('')
  const [provinceId, setProvinceId] = useState('')
  const [statusFilter, setStatusFilter] = useState<DestinationStatusFilter>(
    DESTINATION_STATUS_FILTER.ACTIVE,
  )

  const countriesQuery = useCountriesQuery({})
  const languagesQuery = useLanguagesQuery(true)
  const countries = countriesQuery.data?.data ?? []
  const provincesQuery = useAllProvincesQuery(countries)
  const provinces = useMemo(() => provincesQuery.data ?? [], [provincesQuery.data])
  const filteredProvinces = useMemo(
    () => provinces.filter((item) => !countryId || item.countryId === countryId),
    [countryId, provinces],
  )
  const { selectedLanguageCode, setSelectedLanguageCode, languageOptions, defaultLanguageCode } =
    useLanguageFilter(languagesQuery.data?.data)
  const activeFilterCount = [
    selectedLanguageCode && selectedLanguageCode !== defaultLanguageCode
      ? selectedLanguageCode
      : '',
    countryId,
    provinceId,
    statusFilter !== DESTINATION_STATUS_FILTER.ALL ? statusFilter : '',
  ].filter(Boolean).length

  const destinationsQuery = useDestinationsQuery({
    current: page,
    pageSize,
    ...(selectedLanguageCode ? { languageCode: selectedLanguageCode } : {}),
    ...(countryId ? { countryId } : {}),
    ...(provinceId ? { provinceId } : {}),
    ...(statusFilter === DESTINATION_STATUS_FILTER.ALL
      ? {}
      : { isActive: statusFilter === DESTINATION_STATUS_FILTER.ACTIVE }),
  })

  const filteredData = useMemo(() => {
    const rawData = destinationsQuery.data?.data.data ?? []
    const keyword = searchTerm.trim().toLowerCase()
    if (!keyword) {
      return rawData
    }
    return rawData.filter((item) => {
      const values = item.localizedData ? Object.values(item.localizedData) : []
      return (
        item.slug.toLowerCase().includes(keyword) ||
        values.some(
          (value) =>
            (value.title ?? '').toLowerCase().includes(keyword) ||
            (value.name ?? '').toLowerCase().includes(keyword) ||
            (value.description ?? '').toLowerCase().includes(keyword),
        )
      )
    })
  }, [destinationsQuery.data?.data.data, searchTerm])
  const currentScope =
    scopeType === AdminScopeType.Tenant && tenantSlug
      ? { type: AdminScopeType.Tenant as const, tenantSlug }
      : { type: AdminScopeType.Super as const }
  const destinationsPath = buildScopedAdminPath(currentScope, '/destinations')
  const destinationCreatePath = buildScopedAdminPath(currentScope, '/destinations/create')

  /**
   * Restores destination filters to the current default language and first page.
   */
  function resetAdvancedFilters() {
    setSelectedLanguageCode(defaultLanguageCode)
    setCountryId('')
    setProvinceId('')
    setStatusFilter(DESTINATION_STATUS_FILTER.ACTIVE)
    setPage(1)
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('sidebar.destinations', { defaultValue: 'Destinations' })}
        breadcrumbs={[{ label: t('sidebar.destinations', { defaultValue: 'Destinations' }) }]}
        actions={
          isSuperAdmin ? (
            <Button onClick={() => void navigate({ to: destinationCreatePath })}>
              <Plus className="mr-1.5 h-4 w-4" />
              {t('common.create')}
            </Button>
          ) : null
        }
      />

      <DataTableToolbar>
        <DataTableFilterField
          label={t('common.search', { defaultValue: 'Search' })}
          className="min-w-[240px]"
        >
          <TenantSearchBar
            placeholder={t('common.search', { defaultValue: 'Search' })}
            onSearch={(value) => {
              setSearchTerm(value)
              setPage(1)
            }}
          />
        </DataTableFilterField>
        <FilterPopover
          title={t('common.filter')}
          resetLabel={t('common.reset')}
          activeCount={activeFilterCount}
          onReset={resetAdvancedFilters}
        >
          <DataTableFilterField label={t('common.language')}>
            <Select
              value={selectedLanguageCode || defaultLanguageCode}
              onValueChange={(value) => {
                setSelectedLanguageCode(value)
                setPage(1)
              }}
              disabled={languageOptions.length < 1}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.language')} />
              </SelectTrigger>
              <SelectContent>
                {languageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('destination.country')}>
            <Select
              value={countryId || DESTINATION_FILTER_VALUE.SELECT_ALL}
              onValueChange={(value) => {
                const nextCountryId = value === DESTINATION_FILTER_VALUE.SELECT_ALL ? '' : value
                setCountryId(nextCountryId)
                setProvinceId('')
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('destination.country')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DESTINATION_FILTER_VALUE.SELECT_ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                {countries.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.nativeName ?? item.name ?? item.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('destination.province')}>
            <Select
              value={provinceId || DESTINATION_FILTER_VALUE.SELECT_ALL}
              onValueChange={(value) => {
                setProvinceId(value === DESTINATION_FILTER_VALUE.SELECT_ALL ? '' : value)
                setPage(1)
              }}
              disabled={!countryId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('destination.province')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DESTINATION_FILTER_VALUE.SELECT_ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                {filteredProvinces.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.nativeName ?? item.name ?? item.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>

          <DataTableFilterField label={t('common.status')}>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as DestinationStatusFilter)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={DESTINATION_STATUS_FILTER.ALL}>
                  {t('common.all', { defaultValue: 'All' })}
                </SelectItem>
                <SelectItem value={DESTINATION_STATUS_FILTER.ACTIVE}>
                  {t('common.active', { defaultValue: 'Active' })}
                </SelectItem>
                <SelectItem value={DESTINATION_STATUS_FILTER.INACTIVE}>
                  {t('common.inactive', { defaultValue: 'Inactive' })}
                </SelectItem>
              </SelectContent>
            </Select>
          </DataTableFilterField>
        </FilterPopover>
      </DataTableToolbar>

      <DestinationListTable
        destinations={filteredData}
        countries={countries}
        provinces={provinces}
        loading={destinationsQuery.isLoading}
        languageCode={selectedLanguageCode || defaultLanguageCode}
        pagination={{
          current: page,
          pageSize,
          total: searchTerm.trim()
            ? filteredData.length
            : (destinationsQuery.data?.data.total ?? 0),
        }}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage)
          setPageSize(nextPageSize)
        }}
        onEdit={(item) =>
          void navigate({
            href: generatePath(`${destinationsPath}/:id/edit`, { id: item.id }),
          })
        }
        onView={(item) =>
          void navigate({
            href: generatePath(`${destinationsPath}/:id/edit`, { id: item.id }),
          })
        }
        readonly={!isSuperAdmin}
      />
    </PageContainer>
  )
}
